import type { ReactNode } from "react";
import { cn } from "@/utils";

interface FieldShellProps {
  id?: string;
  label?: ReactNode;
  required?: boolean;
  hint?: ReactNode;
  error?: string | null;
  /** Element rendered to the right of the label (e.g. a counter or helper link). */
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}

/**
 * Label + hint/error scaffold shared by every field in the design system.
 * Keeps spacing, the electric required-asterisk, and error styling consistent
 * so no page has to hand-roll its own `<label>` markup.
 */
export function FieldShell({
  id,
  label,
  required,
  hint,
  error,
  action,
  className,
  children,
}: FieldShellProps) {
  return (
    <div className={cn("space-y-1.5", className)}>
      {(label || action) && (
        <div className="flex items-center justify-between gap-2">
          {label ? (
            <label
              htmlFor={id}
              className="text-[13px] font-semibold leading-none"
              style={{ color: "var(--landing-text)" }}
            >
              {label}
              {required && <span className="ml-1 text-[var(--a2m-sig-ink-hi)]">*</span>}
            </label>
          ) : (
            <span />
          )}
          {action}
        </div>
      )}
      {children}
      {error ? (
        <p className="text-xs font-medium text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-xs" style={{ color: "var(--landing-text-muted)" }}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}
